import { SsaFunction, SsaBlock, SsaInstr, SsaValue, SsaVariable, SsaConst, COMPARE_INVERT } from './SsaTypes';
import { DominanceInfo, dominates, findNaturalLoops, NaturalLoop } from './DominanceTree';
import { postProcessNode } from './StructuredPostProcessing';

export type StructuredNode =
  | { kind: 'sequence'; children: StructuredNode[] }
  | { kind: 'block'; body: SsaInstr[] }
  | { kind: 'if'; condition: SsaValue; thenBody: StructuredNode; elseBody: StructuredNode | null }
  | { kind: 'while'; condition: SsaValue | null; body: StructuredNode }
  | { kind: 'do_while'; body: StructuredNode; condition: SsaValue }
  | { kind: 'switch'; selector: SsaValue; cases: { values: number[]; body: StructuredNode }[]; defaultBody: StructuredNode }
  | { kind: 'break' }
  | { kind: 'continue' }
  | { kind: 'return'; value: SsaValue | null }
  | { kind: 'unreachable' }
  | { kind: 'labeled_block'; label: string; body: StructuredNode }
  | { kind: 'labeled_break'; label: string }
  | { kind: 'labeled_continue'; label: string }
  | { kind: 'label'; name: string }
  | { kind: 'goto'; target: string };

// Thrown when a region cannot be reduced to structured control flow. The function-level
// structurer catches it and re-emits the whole function with explicit labels and gotos.
export class UnstructuredControlFlowError extends Error {}

/**
 * Recovers high-level control flow (if/while/switch/break/continue) from the SSA CFG.
 * Identifies natural loops via dominance, then walks the CFG in dominator-tree order,
 * classifying edges as loop-back, loop-exit, if-then-else, or linear fall-through.
 * Unstructured regions fall back to labeled blocks.
 */
export function structureFunction(
  ssaFunc: SsaFunction,
  dominance: DominanceInfo,
): StructuredNode {
  return new StructuralAnalyzer(ssaFunc, dominance).structure();
}

interface StructuringResult {
  node: StructuredNode;
  mergeBlockId: number | null;
}

interface LoopBodyStep {
  next: number | null;
  stop: boolean;
}

const LOOP_BODY_STOP: LoopBodyStep = { next: null, stop: true };

class StructuralAnalyzer {
  private readonly blockMap = new Map<number, SsaBlock>();
  private readonly loopsByHeader = new Map<number, NaturalLoop>();
  private readonly processed = new Set<number>();
  private readonly defMap = new Map<number, SsaInstr>();
  private nextVarId: number;

  constructor(
    private readonly ssaFunc: SsaFunction,
    private readonly dominance: DominanceInfo,
  ) {
    for (const block of ssaFunc.blocks) {
      this.blockMap.set(block.id, block);
    }
    for (const loop of findNaturalLoops(ssaFunc.blocks, dominance.immediateDominator)) {
      this.loopsByHeader.set(loop.headerId, loop);
    }
    for (const block of ssaFunc.blocks) {
      for (const instr of block.instructions) {
        if ('result' in instr && instr.result) {
          this.defMap.set(instr.result.id, instr);
        }
      }
    }
    this.nextVarId = ssaFunc.variables.length > 0
      ? ssaFunc.variables.reduce((maxId, variable) => Math.max(maxId, variable.id), 0) + 1
      : 1000;
  }

  structure(): StructuredNode {
    let result: StructuredNode;
    try {
      result = this.structureRegion(this.ssaFunc.entryBlockId, this.ssaFunc.exitBlockId);
    } catch (error) {
      if (error instanceof UnstructuredControlFlowError) {
        return this.structureByGoto();
      }
      throw error;
    }
    // If any reachable block was left unstructured, the region was irreducible; emit the
    // whole function with explicit gotos rather than dropping or misplacing those blocks.
    if (this.hasUnstructuredBlocks()) {
      return this.structureByGoto();
    }
    return postProcessNode(result, condition => this.negateCondition(condition));
  }

  private hasUnstructuredBlocks(): boolean {
    return this.ssaFunc.blocks.some(block =>
      block.id !== this.ssaFunc.exitBlockId &&
      block.instructions.length > 0 &&
      !this.processed.has(block.id));
  }

  // Last-resort lowering for control flow that can't be reduced to if/while/switch:
  // every block becomes a label, and its branches become explicit gotos (or an if/switch
  // over gotos). Correct for any CFG, including irreducible ones.
  private structureByGoto(): StructuredNode {
    const exitId = this.ssaFunc.exitBlockId;
    const ordered = [
      ...this.ssaFunc.blocks.filter(block => block.id === this.ssaFunc.entryBlockId),
      ...this.ssaFunc.blocks.filter(block => block.id !== this.ssaFunc.entryBlockId && block.id !== exitId),
    ];
    const children: StructuredNode[] = [];
    for (const block of ordered) {
      children.push({ kind: 'label', name: `block_${block.id}` });
      this.appendGotoBlockBody(block, children);
    }
    children.push({ kind: 'label', name: `block_${exitId}` });
    return { kind: 'sequence', children };
  }

  // Walks a block's instructions in order, emitting plain statements as block nodes and
  // every branch as a goto. Handles a conditional that sits mid-block (its false edge is
  // the in-block fall-through) as well as one that terminates the block.
  private appendGotoBlockBody(block: SsaBlock, children: StructuredNode[]): void {
    const gotoTo = (blockId: number): StructuredNode => ({ kind: 'goto', target: `block_${blockId}` });
    let pending: SsaInstr[] = [];
    const flush = (): void => {
      if (pending.length > 0) {
        children.push({ kind: 'block', body: pending });
        pending = [];
      }
    };
    block.instructions.forEach((instruction, index) => {
      const isLast = index === block.instructions.length - 1;
      if (instruction.kind === 'branch_if') {
        flush();
        children.push({
          kind: 'if', condition: instruction.condition,
          thenBody: gotoTo(instruction.trueTarget),
          elseBody: isLast ? gotoTo(instruction.falseTarget) : null,
        });
      } else if (instruction.kind === 'branch_table') {
        flush();
        const caseValues = this.groupCaseValues(instruction.targets);
        const cases = [...this.uniqueSwitchTargets(instruction)].map(target => ({ values: caseValues.get(target) || [], body: gotoTo(target) }));
        children.push({ kind: 'switch', selector: instruction.selector, cases, defaultBody: gotoTo(instruction.defaultTarget) });
      } else if (instruction.kind === 'branch') {
        flush();
        children.push(gotoTo(instruction.target));
      } else {
        pending.push(instruction);
      }
    });
    flush();
    const last = block.instructions[block.instructions.length - 1];
    const transfersControl = last && (last.kind === 'branch' || last.kind === 'branch_if' ||
      last.kind === 'branch_table' || last.kind === 'return' || last.kind === 'unreachable');
    if (!transfersControl) {
      children.push(gotoTo(block.successors.length > 0 ? block.successors[0] : this.ssaFunc.exitBlockId));
    }
  }

  private realPredecessorCount(block: SsaBlock): number {
    return block.predecessors.filter(predId => {
      const predBlock = this.blockMap.get(predId);
      return predBlock && predBlock.successors.includes(block.id);
    }).length;
  }

  private isTerminatorKind(kind: string): boolean {
    return kind === 'branch' || kind === 'branch_if' || kind === 'branch_table' || kind === 'return' || kind === 'unreachable';
  }

  private insertBeforeTerminator(block: SsaBlock, instruction: SsaInstr): void {
    const lastInstruction = block.instructions[block.instructions.length - 1];
    if (lastInstruction && this.isTerminatorKind(lastInstruction.kind)) {
      block.instructions.splice(block.instructions.length - 1, 0, instruction);
    } else {
      block.instructions.push(instruction);
    }
  }

  private findBlockContaining(varId: number): SsaBlock | null {
    for (const block of this.ssaFunc.blocks) {
      for (const instr of block.instructions) {
        if ('result' in instr && instr.result && instr.result.id === varId) {
          return block;
        }
      }
    }
    return null;
  }

  // Whether `value` is a variable whose definition lives in the given block. A constant has no
  // defining block and is never "in" one.
  private isDefinedInBlock(value: SsaValue, blockId: number): boolean {
    if (!('id' in value) || 'kind' in value) {
      return false;
    }
    return this.findBlockContaining(value.id)?.id === blockId;
  }

  private negateCondition(condition: SsaValue): SsaValue {
    if ('kind' in condition && condition.kind === 'const') {
      return { kind: 'const', value: condition.value ? 0 : 1, type: 'i32' } as SsaConst;
    }
    if ('id' in condition && !('kind' in condition)) {
      const defInstr = this.defMap.get(condition.id);
      // Invert a comparison in place (`a < b` -> `a >= b`) when its definition is locatable,
      // which keeps the operands in scope; otherwise fall through to a `!` of the condition.
      if (defInstr && defInstr.kind === 'compare' && COMPARE_INVERT[defInstr.op]) {
        const ownerBlock = this.findBlockContaining(condition.id);
        if (ownerBlock) {
          const newVar: SsaVariable = { id: this.nextVarId++, name: `neg_${condition.id}`, type: 'i32', definedInBlock: -1 };
          const newInstr: SsaInstr = { kind: 'compare', result: newVar, op: COMPARE_INVERT[defInstr.op], left: defInstr.left, right: defInstr.right };
          this.insertBeforeTerminator(ownerBlock, newInstr);
          this.defMap.set(newVar.id, newInstr);
          return newVar;
        }
      }
      if (defInstr && defInstr.kind === 'unary' && defInstr.op === '!') {
        return defInstr.operand;
      }
    }
    return this.notExpression(condition);
  }

  // Builds `!condition` as a unary instruction and inserts its definition where the
  // condition is defined, or — when the condition has no defining instruction (a
  // parameter or other entry-live value) — at the entry block, which dominates every
  // use. Without this fallback the `!` definition was dropped and the result emitted as
  // an undefined variable.
  private notExpression(condition: SsaValue): SsaVariable {
    const varId = this.nextVarId++;
    const newVar: SsaVariable = { id: varId, name: `neg_${varId}`, type: 'i32', definedInBlock: -1 };
    const newInstr: SsaInstr = { kind: 'unary', result: newVar, op: '!', operand: condition };
    const ownerBlock = ('id' in condition && !('kind' in condition)) ? this.findBlockContaining(condition.id) : null;
    const targetBlock = ownerBlock ?? this.ssaFunc.blocks[0];
    if (targetBlock) {
      this.insertBeforeTerminator(targetBlock, newInstr);
    }
    this.defMap.set(newVar.id, newInstr);
    return newVar;
  }

  private isExitTarget(blockId: number): boolean {
    if (blockId === this.ssaFunc.exitBlockId) {
      return true;
    }
    const block = this.blockMap.get(blockId);
    if (!block) {
      return false;
    }
    if (block.instructions.length === 0 && block.successors.length === 1 && block.successors[0] === this.ssaFunc.exitBlockId) {
      return true;
    }
    if (block.successors.includes(this.ssaFunc.exitBlockId)) {
      const substantiveInstrs = block.instructions.filter(instr => instr.kind !== 'return' && instr.kind !== 'phi');
      if (substantiveInstrs.length === 0) {
        return true;
      }
    }
    return false;
  }

  private structureRegion(blockId: number, regionEnd: number | null, virtuallyProcessed?: Set<number>): StructuredNode {
    if (this.processed.has(blockId)) {
      return { kind: 'sequence', children: [] };
    }
    if (!this.blockMap.has(blockId)) {
      return { kind: 'sequence', children: [] };
    }
    const loop = this.loopsByHeader.get(blockId);
    if (loop) {
      return this.structureLoop(blockId, loop, regionEnd);
    }
    return this.structureLinear(blockId, regionEnd, virtuallyProcessed);
  }

  private structureLinear(blockId: number, regionEnd: number | null, virtuallyProcessed?: Set<number>): StructuredNode {
    const children: StructuredNode[] = [];
    let currentBlockId: number | null = blockId;
    while (currentBlockId !== null && currentBlockId !== regionEnd) {
      if (this.processed.has(currentBlockId)) {
        const skipTarget = this.skipToPostDominator(currentBlockId, regionEnd);
        if (skipTarget === null) { break; }
        currentBlockId = skipTarget;
        continue;
      }
      const block = this.blockMap.get(currentBlockId);
      if (!block) { break; }
      const loop = this.loopsByHeader.get(currentBlockId);
      if (loop && children.length > 0) {
        children.push(this.structureLoop(currentBlockId, loop, regionEnd));
        currentBlockId = this.findSingleExit(loop);
        continue;
      }
      this.processed.add(currentBlockId);
      currentBlockId = this.structureLinearStep(block, currentBlockId, regionEnd, virtuallyProcessed, children);
    }
    return children.length === 1 ? children[0] : { kind: 'sequence', children };
  }

  // When a block was already consumed by a nested structuring call, skip to its
  // post-dominator so downstream unprocessed blocks still get structured.
  private skipToPostDominator(blockId: number, regionEnd: number | null): number | null {
    const skipTarget = this.dominance.postImmediateDominator.get(blockId);
    if (skipTarget !== undefined && skipTarget !== blockId && skipTarget !== regionEnd && !this.processed.has(skipTarget)) {
      return skipTarget;
    }
    return null;
  }

  private structureLinearStep(block: SsaBlock, currentBlockId: number, regionEnd: number | null, virtuallyProcessed: Set<number> | undefined, children: StructuredNode[]): number | null {
    const terminator = findTerminator(block);
    if (!terminator) {
      children.push(blockToNode(block));
      return block.successors.length >= 1 ? block.successors[0] : null;
    }
    if (terminator.kind === 'return' || terminator.kind === 'unreachable') {
      children.push(blockToNode(block));
      return null;
    }
    if (terminator.kind === 'branch') {
      return this.structureLinearBranch(block, terminator, currentBlockId, virtuallyProcessed, children);
    }
    if (terminator.kind === 'branch_if') {
      const ifResult = this.structureIf(block, terminator, regionEnd);
      children.push(ifResult.node);
      return ifResult.mergeBlockId;
    }
    if (terminator.kind === 'branch_table') {
      children.push(blockToNodeWithoutTerminator(block));
      const switchResult = this.structureSwitch(terminator, regionEnd, currentBlockId);
      children.push(switchResult.node);
      return switchResult.mergeBlockId;
    }
    children.push(blockToNode(block));
    return null;
  }

  private structureLinearBranch(block: SsaBlock, terminator: Extract<SsaInstr, { kind: 'branch' }>, currentBlockId: number, virtuallyProcessed: Set<number> | undefined, children: StructuredNode[]): number | null {
    children.push(blockToNodeWithoutTerminator(block));
    if (this.isExitTarget(terminator.target)) {
      children.push({ kind: 'return', value: null });
      return null;
    }
    const targetBlock = this.blockMap.get(terminator.target);
    // Merge-point heuristic: defer a branch target that still has unprocessed real
    // predecessors and is not dominated here, so one arm doesn't consume the merge.
    if (targetBlock && targetBlock.predecessors.length > 1) {
      const realPreds = targetBlock.predecessors.filter(predId => {
        const predBlock = this.blockMap.get(predId);
        return predBlock && predBlock.successors.includes(terminator.target);
      });
      const unprocessedPreds = realPreds.filter(predId =>
        !this.processed.has(predId) && !(virtuallyProcessed && virtuallyProcessed.has(predId)));
      if (unprocessedPreds.length > 0 && !dominates(this.dominance.immediateDominator, currentBlockId, terminator.target)) {
        return null;
      }
    }
    return terminator.target;
  }

  private structureAfterWhileLoop(whileNode: StructuredNode, loop: NaturalLoop, primaryExitId: number, regionEnd: number | null): StructuredNode {
    const afterChildren: StructuredNode[] = [whileNode];
    const postDom = this.dominance.postImmediateDominator.get(loop.headerId);
    const convergenceId = (postDom !== undefined && !loop.bodyIds.has(postDom)) ? postDom : null;

    if (primaryExitId !== regionEnd && !this.processed.has(primaryExitId) && primaryExitId !== convergenceId) {
      afterChildren.push(this.structureRegion(primaryExitId, convergenceId ?? regionEnd));
    }
    for (const exitId of loop.exitIds) {
      if (exitId === primaryExitId || exitId === regionEnd || this.processed.has(exitId) || exitId === convergenceId) { continue; }
      afterChildren.push(this.structureRegion(exitId, convergenceId ?? regionEnd));
    }
    if (convergenceId !== null && convergenceId !== regionEnd && !this.processed.has(convergenceId)) {
      afterChildren.push(this.structureRegion(convergenceId, regionEnd));
    }
    return afterChildren.length === 1 ? whileNode : { kind: 'sequence', children: afterChildren };
  }

  private structureLoop(headerId: number, loop: NaturalLoop, regionEnd: number | null): StructuredNode {
    this.processed.add(headerId);
    const headerBlock = this.blockMap.get(headerId)!;
    const terminator = findTerminator(headerBlock);

    if (terminator && terminator.kind === 'branch_if') {
      const trueInLoop = loop.bodyIds.has(terminator.trueTarget);
      const falseInLoop = loop.bodyIds.has(terminator.falseTarget);
      if (trueInLoop !== falseInLoop) {
        const continueTarget = trueInLoop ? terminator.trueTarget : terminator.falseTarget;
        const exitTarget = trueInLoop ? terminator.falseTarget : terminator.trueTarget;
        // A single block that branches back to itself is a do/while: the body runs, then tests at
        // the end. do/while is correct whether or not the test inlines (it is evaluated after the
        // body computes it) and avoids the duplicate body and read-before-test a pre-tested while
        // would give here.
        if (continueTarget === headerId && loop.bodyIds.size === 1) {
          const continueCondition = trueInLoop ? terminator.condition : this.negateCondition(terminator.condition);
          const doWhileNode: StructuredNode = { kind: 'do_while', body: blockToNodeWithoutTerminator(headerBlock), condition: continueCondition };
          return this.structureAfterWhileLoop(doWhileNode, loop, exitTarget, regionEnd);
        }
        const body = this.structureLoopBody(continueTarget, headerId, loop);
        // A pre-tested `while (cond)` reads the test before the body runs. That is wrong when the
        // header itself computes the test and the body runs after it — a separate body block whose
        // loop header defines the condition. In that case emit a header-tested loop, which
        // `recoverWhileConditions` folds back to `while (cond)` once the header inlines away;
        // otherwise (condition available on entry, benign header) keep the plain pre-tested while.
        if (continueTarget !== headerId && this.isDefinedInBlock(terminator.condition, headerId)) {
          const exitCondition = trueInLoop ? this.negateCondition(terminator.condition) : terminator.condition;
          const whileNode = this.buildHeaderTestedLoop(headerBlock, exitCondition, body);
          return this.structureAfterWhileLoop(whileNode, loop, exitTarget, regionEnd);
        }
        const condition = trueInLoop ? terminator.condition : this.negateCondition(terminator.condition);
        const whileNode: StructuredNode = { kind: 'while', condition, body: prependNode(blockToNodeWithoutTerminator(headerBlock), body) };
        return this.structureAfterWhileLoop(whileNode, loop, exitTarget, regionEnd);
      }
    }

    const whileNode: StructuredNode = { kind: 'while', condition: null, body: this.structureLoopBody(headerId, headerId, loop) };
    return this.structureAfterInfiniteLoop(whileNode, loop, headerId, regionEnd);
  }

  // A loop whose header computes its own test: the header runs every iteration before the test,
  // so it cannot be the `while` condition (that would read the test before the header sets it,
  // uninitialized on entry). Emit `while (true) { header; if (exit) break; body }`; the lowered
  // `recoverWhileConditions` folds this back to `while (!exit)` once the header inlines away.
  private buildHeaderTestedLoop(headerBlock: SsaBlock, exitCondition: SsaValue, body: StructuredNode): StructuredNode {
    const breakGuard: StructuredNode = { kind: 'if', condition: exitCondition, thenBody: { kind: 'break' }, elseBody: null };
    const header = blockToNodeWithoutTerminator(headerBlock);
    const children: StructuredNode[] = [];
    if (!(header.kind === 'sequence' && header.children.length === 0)) {
      children.push(header);
    }
    children.push(breakGuard, body);
    return { kind: 'while', condition: null, body: { kind: 'sequence', children } };
  }

  private structureAfterInfiniteLoop(whileNode: StructuredNode, loop: NaturalLoop, headerId: number, regionEnd: number | null): StructuredNode {
    const afterChildren: StructuredNode[] = [whileNode];
    if (loop.exitIds.size > 0) {
      const postDom = this.dominance.postImmediateDominator.get(headerId);
      const convergenceId = (postDom !== undefined && !loop.bodyIds.has(postDom)) ? postDom : null;
      for (const exitId of loop.exitIds) {
        if (exitId === regionEnd || this.processed.has(exitId) || exitId === convergenceId) { continue; }
        afterChildren.push(this.structureRegion(exitId, convergenceId ?? regionEnd));
      }
      if (convergenceId !== null && convergenceId !== regionEnd && !this.processed.has(convergenceId)) {
        afterChildren.push(this.structureRegion(convergenceId, regionEnd));
      }
    }
    return afterChildren.length === 1 ? whileNode : { kind: 'sequence', children: afterChildren };
  }

  private structureLoopBody(startId: number, headerId: number, loop: NaturalLoop): StructuredNode {
    const children: StructuredNode[] = [];
    let currentBlockId: number | null = startId;
    while (currentBlockId !== null) {
      if (currentBlockId === headerId && children.length > 0) { children.push({ kind: 'continue' }); break; }
      if (!loop.bodyIds.has(currentBlockId)) { children.push({ kind: 'break' }); break; }
      if (this.processed.has(currentBlockId) && currentBlockId !== startId) { break; }
      const block = this.blockMap.get(currentBlockId);
      if (!block) { break; }
      this.processed.add(currentBlockId);
      const step = this.structureLoopBodyStep(block, currentBlockId, headerId, loop, children);
      if (step.stop) { break; }
      currentBlockId = step.next;
    }
    return children.length === 1 ? children[0] : { kind: 'sequence', children };
  }

  private structureLoopBodyStep(block: SsaBlock, currentBlockId: number, headerId: number, loop: NaturalLoop, children: StructuredNode[]): LoopBodyStep {
    const terminator = findTerminator(block);
    if (!terminator) {
      return this.loopBodyNoTerminator(block, headerId, loop, children);
    }
    if (terminator.kind === 'return' || terminator.kind === 'unreachable') {
      children.push(blockToNode(block));
      return LOOP_BODY_STOP;
    }
    if (terminator.kind === 'branch') {
      return this.loopBodyBranch(block, terminator, headerId, loop, children);
    }
    if (terminator.kind === 'branch_if') {
      return this.loopBodyBranchIf(block, terminator, headerId, loop, children);
    }
    if (terminator.kind === 'branch_table') {
      return this.loopBodyBranchTable(block, terminator, currentBlockId, headerId, loop, children);
    }
    children.push(blockToNode(block));
    return LOOP_BODY_STOP;
  }

  private loopBodyNoTerminator(block: SsaBlock, headerId: number, loop: NaturalLoop, children: StructuredNode[]): LoopBodyStep {
    children.push(blockToNode(block));
    if (block.successors.length === 1) {
      return { next: block.successors[0], stop: false };
    }
    if (block.successors.length !== 2) {
      return LOOP_BODY_STOP;
    }
    const normalTarget = block.successors[0];
    const catchTarget = block.successors[1];
    if (!loop.bodyIds.has(normalTarget) || normalTarget === headerId) {
      children.push({ kind: normalTarget === headerId ? 'continue' : 'break' });
      return { next: catchTarget, stop: false };
    }
    if (!loop.bodyIds.has(catchTarget) || catchTarget === headerId) {
      return { next: normalTarget, stop: false };
    }
    children.push(this.structureRegion(catchTarget, null));
    return { next: normalTarget, stop: false };
  }

  private loopBodyBranch(block: SsaBlock, terminator: Extract<SsaInstr, { kind: 'branch' }>, headerId: number, loop: NaturalLoop, children: StructuredNode[]): LoopBodyStep {
    children.push(blockToNodeWithoutTerminator(block));
    if (terminator.target === headerId) {
      children.push({ kind: 'continue' });
      return LOOP_BODY_STOP;
    }
    if (!loop.bodyIds.has(terminator.target)) {
      children.push({ kind: 'break' });
      return LOOP_BODY_STOP;
    }
    return { next: terminator.target, stop: false };
  }

  private loopBodyBranchIf(block: SsaBlock, terminator: Extract<SsaInstr, { kind: 'branch_if' }>, headerId: number, loop: NaturalLoop, children: StructuredNode[]): LoopBodyStep {
    const trueIsExit = !loop.bodyIds.has(terminator.trueTarget) || terminator.trueTarget === headerId;
    const falseIsExit = !loop.bodyIds.has(terminator.falseTarget) || terminator.falseTarget === headerId;
    if (trueIsExit && !falseIsExit) {
      children.push(blockToNodeWithoutTerminator(block));
      children.push(this.loopExitIf(terminator.condition, terminator.trueTarget, headerId));
      return { next: terminator.falseTarget, stop: false };
    }
    if (falseIsExit && !trueIsExit) {
      children.push(blockToNodeWithoutTerminator(block));
      children.push(this.loopExitIf(this.negateCondition(terminator.condition), terminator.falseTarget, headerId));
      return { next: terminator.trueTarget, stop: false };
    }
    const ifResult = this.structureIf(block, terminator, null);
    children.push(ifResult.node);
    let next = ifResult.mergeBlockId;
    if (next === null) {
      const postDom = this.dominance.postImmediateDominator.get(block.id);
      if (postDom !== undefined && loop.bodyIds.has(postDom) && !this.processed.has(postDom)) { next = postDom; }
    }
    return { next, stop: false };
  }

  private loopExitIf(condition: SsaValue, target: number, headerId: number): StructuredNode {
    return { kind: 'if', condition, thenBody: { kind: target === headerId ? 'continue' : 'break' }, elseBody: null };
  }

  private loopBodyBranchTable(block: SsaBlock, terminator: Extract<SsaInstr, { kind: 'branch_table' }>, currentBlockId: number, headerId: number, loop: NaturalLoop, children: StructuredNode[]): LoopBodyStep {
    children.push(blockToNodeWithoutTerminator(block));
    const caseValuesByTarget = this.groupCaseValues(terminator.targets);
    const cases: { values: number[]; body: StructuredNode }[] = [];
    for (const targetId of this.uniqueSwitchTargets(terminator)) {
      cases.push(this.loopSwitchCase(targetId, caseValuesByTarget.get(targetId) || [], headerId, loop));
    }
    children.push({ kind: 'switch', selector: terminator.selector, cases, defaultBody: this.loopSwitchDefault(terminator.defaultTarget, headerId, loop) });
    const postDom = this.dominance.postImmediateDominator.get(currentBlockId);
    const next = (postDom !== undefined && loop.bodyIds.has(postDom) && !this.processed.has(postDom)) ? postDom : null;
    return { next, stop: false };
  }

  private loopSwitchCase(targetId: number, caseValues: number[], headerId: number, loop: NaturalLoop): { values: number[]; body: StructuredNode } {
    if (targetId === headerId) { return { values: caseValues, body: { kind: 'continue' } }; }
    if (!loop.bodyIds.has(targetId)) { return { values: caseValues, body: { kind: 'break' } }; }
    if (this.processed.has(targetId)) { return { values: caseValues, body: { kind: 'sequence', children: [] } }; }
    return { values: caseValues, body: this.structureRegion(targetId, null) };
  }

  private loopSwitchDefault(defaultTarget: number, headerId: number, loop: NaturalLoop): StructuredNode {
    if (defaultTarget === headerId) { return { kind: 'continue' }; }
    if (!loop.bodyIds.has(defaultTarget)) { return { kind: 'break' }; }
    if (!this.processed.has(defaultTarget)) { return this.structureRegion(defaultTarget, null); }
    return { kind: 'sequence', children: [] };
  }

  private groupCaseValues(targets: number[]): Map<number, number[]> {
    const caseValuesByTarget = new Map<number, number[]>();
    for (let caseIndex = 0; caseIndex < targets.length; caseIndex++) {
      const targetId = targets[caseIndex];
      if (!caseValuesByTarget.has(targetId)) { caseValuesByTarget.set(targetId, []); }
      caseValuesByTarget.get(targetId)!.push(caseIndex);
    }
    return caseValuesByTarget;
  }

  private uniqueSwitchTargets(terminator: Extract<SsaInstr, { kind: 'branch_table' }>): Set<number> {
    const uniqueTargetIds = new Set<number>();
    for (const targetId of terminator.targets) {
      if (targetId !== terminator.defaultTarget) { uniqueTargetIds.add(targetId); }
    }
    return uniqueTargetIds;
  }

  private findMergePoint(targetA: number, targetB: number): number | null {
    const reachableA = new Set<number>();
    const worklistA: number[] = [targetA];
    while (worklistA.length > 0) {
      const blockId = worklistA.pop()!;
      if (reachableA.has(blockId)) { continue; }
      reachableA.add(blockId);
      const block = this.blockMap.get(blockId);
      if (block) { for (const successorId of block.successors) { worklistA.push(successorId); } }
    }
    const visited = new Set<number>();
    const worklistB: number[] = [targetB];
    while (worklistB.length > 0) {
      const blockId = worklistB.pop()!;
      if (visited.has(blockId)) { continue; }
      visited.add(blockId);
      if (reachableA.has(blockId)) { return blockId; }
      const block = this.blockMap.get(blockId);
      if (block) { for (const successorId of block.successors) { worklistB.push(successorId); } }
    }
    return null;
  }

  private structureSwitch(terminator: Extract<SsaInstr, { kind: 'branch_table' }>, regionEnd: number | null, dispatchBlockId: number): StructuringResult {
    const caseValuesByTarget = this.groupCaseValues(terminator.targets);
    const uniqueTargetIds = this.uniqueSwitchTargets(terminator);
    const allTargetIds = new Set([...uniqueTargetIds, terminator.defaultTarget]);

    let mergeBlockId: number | null = null;
    const postDomCandidate = this.dominance.postImmediateDominator.get(dispatchBlockId);
    if (postDomCandidate !== undefined && postDomCandidate !== dispatchBlockId && !allTargetIds.has(postDomCandidate)) {
      mergeBlockId = postDomCandidate;
    }

    // Pre-mark all switch targets so the merge-point heuristic allows following
    // into blocks whose only unprocessed predecessors are other switch targets.
    const switchTargetSet = new Set(allTargetIds);
    const cases: { values: number[]; body: StructuredNode }[] = [];
    for (const targetId of uniqueTargetIds) {
      const caseValues = caseValuesByTarget.get(targetId) || [];
      const body = this.processed.has(targetId)
        ? { kind: 'sequence' as const, children: [] }
        : this.structureRegion(targetId, mergeBlockId ?? regionEnd, switchTargetSet);
      cases.push({ values: caseValues, body });
    }
    const defaultBody = this.processed.has(terminator.defaultTarget)
      ? { kind: 'sequence' as const, children: [] }
      : this.structureRegion(terminator.defaultTarget, mergeBlockId ?? regionEnd, switchTargetSet);

    return { node: { kind: 'switch', selector: terminator.selector, cases, defaultBody }, mergeBlockId };
  }

  private structureIf(block: SsaBlock, terminator: Extract<SsaInstr, { kind: 'branch_if' }>, regionEnd: number | null): StructuringResult {
    const { trueTarget, falseTarget, condition } = terminator;
    const preBody = blockToNodeWithoutTerminator(block);
    const trueBlock = this.blockMap.get(trueTarget);
    const falseBlock = this.blockMap.get(falseTarget);
    const trueInlineable = !!trueBlock && this.realPredecessorCount(trueBlock) === 1 && !this.processed.has(trueTarget);
    const falseInlineable = !!falseBlock && this.realPredecessorCount(falseBlock) === 1 && !this.processed.has(falseTarget);
    const mergePoint = this.dominance.postImmediateDominator.get(block.id) ?? this.findMergePoint(trueTarget, falseTarget);

    if (trueInlineable && falseInlineable) {
      return this.ifElseToMerge(preBody, condition, trueTarget, falseTarget, mergePoint, regionEnd);
    }
    if (trueInlineable) {
      return this.ifThenToMerge(preBody, condition, false, trueTarget, falseTarget, regionEnd);
    }
    if (falseInlineable) {
      return this.ifThenToMerge(preBody, condition, true, falseTarget, trueTarget, regionEnd);
    }
    if (mergePoint !== null && mergePoint !== trueTarget && mergePoint !== falseTarget) {
      return this.ifElseToMerge(preBody, condition, trueTarget, falseTarget, mergePoint, regionEnd);
    }
    if (mergePoint === falseTarget && !this.processed.has(trueTarget)) {
      return this.ifThenToMerge(preBody, condition, false, trueTarget, falseTarget, regionEnd);
    }
    if (mergePoint === trueTarget && !this.processed.has(falseTarget)) {
      return this.ifThenToMerge(preBody, condition, true, falseTarget, trueTarget, regionEnd);
    }
    return { node: blockToNode(block), mergeBlockId: null };
  }

  // if (cond) { then } else { else }, both arms structured up to the merge point.
  private ifElseToMerge(preBody: StructuredNode, condition: SsaValue, trueTarget: number, falseTarget: number, mergePoint: number | null, regionEnd: number | null): StructuringResult {
    const ifNode: StructuredNode = {
      kind: 'if', condition,
      thenBody: this.structureRegion(trueTarget, mergePoint),
      elseBody: this.structureRegion(falseTarget, mergePoint),
    };
    const nextId = mergePoint !== null && mergePoint !== regionEnd && !this.processed.has(mergePoint) ? mergePoint : null;
    return { node: prependNode(preBody, ifNode), mergeBlockId: nextId };
  }

  // if (cond) { then }, with the other target as the merge point.
  private ifThenToMerge(preBody: StructuredNode, rawCondition: SsaValue, negate: boolean, thenTarget: number, mergeTarget: number, regionEnd: number | null): StructuringResult {
    // Structure the branch before negating: negateCondition allocates a temp id,
    // and the original code structured the region first.
    const thenBody = this.structureRegion(thenTarget, mergeTarget);
    const condition = negate ? this.negateCondition(rawCondition) : rawCondition;
    const ifNode: StructuredNode = { kind: 'if', condition, thenBody, elseBody: null };
    const mergeId = mergeTarget !== regionEnd && !this.processed.has(mergeTarget) ? mergeTarget : null;
    return { node: prependNode(preBody, ifNode), mergeBlockId: mergeId };
  }

  private findSingleExit(loop: NaturalLoop): number | null {
    if (loop.exitIds.size === 0) {
      return null;
    }
    if (loop.exitIds.size === 1) {
      return loop.exitIds.values().next().value ?? null;
    }
    const postDom = this.dominance.postImmediateDominator.get(loop.headerId);
    if (postDom !== undefined && !loop.bodyIds.has(postDom)) {
      return postDom;
    }
    return loop.exitIds.values().next().value ?? null;
  }
}

function findTerminator(block: SsaBlock): SsaInstr | null {
  for (let index = block.instructions.length - 1; index >= 0; index--) {
    const instruction = block.instructions[index];
    if (instruction.kind === 'branch' || instruction.kind === 'branch_if' ||
        instruction.kind === 'branch_table' || instruction.kind === 'return' ||
        instruction.kind === 'unreachable') {
      return instruction;
    }
  }
  return null;
}

function blockToNode(block: SsaBlock): StructuredNode {
  if (block.instructions.length === 0) {
    return { kind: 'sequence', children: [] };
  }
  // A conditional/multi-way branch emitted as a plain block would be dropped by lowering,
  // silently losing the branch — including when it sits mid-block (e.g. `branch_if` then a
  // fall-through `return`). Reaching here means the block isn't structurable; fail so the
  // function-level structurer re-emits it with explicit gotos instead of erasing control flow.
  const conditional = block.instructions.find(instruction =>
    instruction.kind === 'branch_if' || instruction.kind === 'branch_table');
  if (conditional) {
    throw new UnstructuredControlFlowError(`unstructured ${conditional.kind} in block ${block.id}`);
  }
  return { kind: 'block', body: block.instructions };
}

function blockToNodeWithoutTerminator(block: SsaBlock): StructuredNode {
  const nonTerminators = block.instructions.filter(instruction =>
    instruction.kind !== 'branch' && instruction.kind !== 'branch_if' &&
    instruction.kind !== 'branch_table'
  );
  if (nonTerminators.length === 0) {
    return { kind: 'sequence', children: [] };
  }
  return { kind: 'block', body: nonTerminators };
}

function prependNode(prefix: StructuredNode, main: StructuredNode): StructuredNode {
  if (prefix.kind === 'sequence' && prefix.children.length === 0) {
    return main;
  }
  if (prefix.kind === 'block' && prefix.body.length === 0) {
    return main;
  }
  return { kind: 'sequence', children: [prefix, main] };
}


